import { useMemo } from 'react';
import { resumeApi } from '@carcareers/store';
import { useConfirmation, useLoading, useSnackbar } from '@carcareers/contexts';
import { isError, getErrorsAsString, getErrors } from '@carcareers/utils';
import { CreateResumeDto } from '@libs/dto';
interface ResumeCallbacks {
  onSuccess?: () => void;
}

export const useResume = () => {
  const { setLoading, setLabel } = useLoading();
  const { confirm } = useConfirmation();
  const { toggleSnackbar } = useSnackbar();
  const [resumeCreate] = resumeApi.useResumeCreateMutation();
  const [resumeDelete] = resumeApi.useResumeDeleteMutation();

  return useMemo(
    () => ({
      createResume: async (resume: FormData & CreateResumeDto, callbacks?: ResumeCallbacks) => {
        if (!resume) {
          toggleSnackbar('warning', 'Resume data is empty.');
          return;
        }

        setLabel('Creating resume...');
        setLoading(true);
        const response = await resumeCreate(resume);

        if (!isError(response)) {
          toggleSnackbar('success', 'Resume created');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else if (getErrors(response).length !== 0) {
          toggleSnackbar('error', getErrorsAsString(response));
        }

        setLoading(false);
        return response;
      },
      deleteResume: async (id?: number, callbacks?: ResumeCallbacks) => {
        if (!id) return;

        const confirmed = await confirm({
          title: 'Delete Resume',
          message: 'Are you sure you want to delete this resume?',
        });

        if (!confirmed) return;

        setLabel('Deleting resume...');
        setLoading(true);

        const response = await resumeDelete(id);
        setLoading(false);

        if (!isError(response)) {
          toggleSnackbar('success', `Deleted resume ${id}`);
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          toggleSnackbar('error', getErrorsAsString(response));
        }

        return response;
      },
    }),
    [resumeCreate, resumeDelete, setLoading, setLabel, confirm, toggleSnackbar]
  );
}; 